/**
 * CronogramaRutas
 * ---------------
 * Vista timeline · eje X = fechas (próximos 30 días) · eje Y = espacios (PDVs).
 * Cada bloque = componentes planificados en ese PDV para esa fecha.
 *
 * Útil para el caso real: "ver de un vistazo qué te falta por montar en YSL/Armani
 * Druni en cada PDV y cuándo está planificado".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCcw, CalendarRange, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";

const DIAS_VISTA = 30;

function fmtDateISO(d) {
  return d.toISOString().slice(0, 10);
}

function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
}

export default function CronogramaRutas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [componentes, setComponentes] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [startDate, setStartDate] = useState(() => fmtDateISO(new Date()));

  /* eslint-disable */
  const load = async () => {
    setLoading(true);
    try {
      const today = startDate;
      const end = new Date(new Date(today).getTime() + DIAS_VISTA * 86400000);
      const endIso = fmtDateISO(end);
      const [cR, rR] = await Promise.all([
        api.get("/componentes/pendientes-montar"),
        api.get("/rutas", { params: { fecha_desde: today, fecha_hasta: endIso, limit: 200 } }),
      ]);
      setComponentes(cR.data || []);
      setRutas(rR.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando cronograma");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [startDate]);
  /* eslint-enable */

  // Fechas del rango
  const fechas = useMemo(() => {
    const out = [];
    const base = new Date(startDate);
    for (let i = 0; i < DIAS_VISTA; i++) {
      out.push(fmtDateISO(new Date(base.getTime() + i * 86400000)));
    }
    return out;
  }, [startDate]);

  // Espacios únicos (de los grupos pendientes-montar + rutas)
  const espacios = useMemo(() => {
    const map = new Map();
    for (const g of componentes) {
      if (g.espacio_id) {
        map.set(g.espacio_id, {
          id: g.espacio_id, nombre: g.espacio_nombre, ciudad: g.espacio_ciudad,
          cliente_nombre: g.cliente_nombre,
        });
      }
    }
    for (const r of rutas) {
      for (const p of r.paradas || []) {
        const eid = p.espacio?.id;
        if (eid && !map.has(eid)) {
          map.set(eid, {
            id: eid, nombre: p.espacio?.nombre, ciudad: p.espacio?.ciudad,
            cliente_nombre: p.cliente?.nombre,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [componentes, rutas]);

  // Matriz: { [espacioId]: { [fecha]: { rutas: [{ruta, parada}], pendientes: [components] } } }
  const matriz = useMemo(() => {
    const m = {};
    for (const e of espacios) m[e.id] = {};
    // Rutas planificadas
    for (const r of rutas) {
      const f = r.fecha;
      if (!f) continue;
      for (const p of r.paradas || []) {
        const eid = p.espacio?.id;
        if (!eid || !m[eid]) continue;
        if (!m[eid][f]) m[eid][f] = { ruta_paradas: [], pendientes: 0 };
        m[eid][f].ruta_paradas.push({
          ruta_id: r.id, ruta_codigo: r.codigo,
          parada: p,
          n_comp: (p.componentes || []).length,
        });
      }
    }
    // Componentes pendientes (sin asignar) — los mostramos como "cola" del PDV
    for (const g of componentes) {
      const f = "_pendiente";
      if (!m[g.espacio_id]) continue;
      m[g.espacio_id][f] = { pendientes: g.componentes.length };
    }
    return m;
  }, [espacios, rutas, componentes]);

  const moveStart = (deltaDays) => {
    const base = new Date(startDate);
    setStartDate(fmtDateISO(new Date(base.getTime() + deltaDays * 86400000)));
  };

  return (
    <div className="space-y-4" data-testid="cronograma-page">
      <PageHeader
        title="📅 Cronograma de Rutas"
        subtitle="Vista timeline · 30 días vista · 1 fila por PDV · cada bloque = componentes planificados"
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => moveStart(-7)} data-testid="cron-prev-week">◀ 7d</Button>
            <input
              type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100"
              data-testid="cron-start-input"
            />
            <Button size="sm" variant="ghost" onClick={() => moveStart(7)} data-testid="cron-next-week">7d ▶</Button>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refrescar
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando timeline…
        </div>
      ) : espacios.length === 0 ? (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center text-zinc-500">
          <CalendarRange className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay rutas ni componentes pendientes en este rango.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-800 rounded-lg bg-zinc-950">
          <table className="text-xs" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead className="bg-zinc-900 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 border-r border-zinc-800 sticky left-0 bg-zinc-900 z-20 min-w-[200px]">
                  PDV
                </th>
                <th className="text-center px-2 py-2 border-r border-zinc-800 bg-amber-500/10 min-w-[80px]">
                  📦 Cola
                </th>
                {fechas.map((f) => {
                  const d = new Date(f + "T00:00:00");
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={f} className={`text-center px-1 py-2 border-r border-zinc-800/60 min-w-[64px] ${isWeekend ? "bg-zinc-900/50" : ""}`}>
                      <div className="text-zinc-500 text-[10px] uppercase">{shortDate(f).split(",")[0]}</div>
                      <div className="text-zinc-200 font-mono text-[11px]">{f.slice(5)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {espacios.map((e) => (
                <tr key={e.id} className="hover:bg-zinc-900/40">
                  <td className="px-3 py-2 border-r border-zinc-800 sticky left-0 bg-zinc-950 z-10">
                    <div className="text-zinc-100 truncate max-w-[180px]">{e.nombre}</div>
                    <div className="text-[10px] text-zinc-500">
                      {e.ciudad} {e.cliente_nombre && `· ${e.cliente_nombre}`}
                    </div>
                  </td>
                  <td className="text-center px-1 py-2 border-r border-zinc-800 bg-amber-500/5">
                    {(matriz[e.id]?._pendiente?.pendientes || 0) > 0 ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-flex items-center justify-center bg-amber-500/30 text-amber-200 rounded font-bold text-xs h-6 px-2">
                          {matriz[e.id]._pendiente.pendientes}
                        </span>
                        <button
                          className="text-[9px] text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5 underline-offset-2 hover:underline"
                          title="Crear nueva ruta a este PDV"
                          onClick={() => navigate(`/rutas/nueva?espacio=${e.id}&fecha=${fechas[0]}`)}
                          data-testid={`crear-ruta-${e.id}`}
                        >
                          <Plus className="w-2.5 h-2.5" /> ruta
                        </button>
                      </div>
                    ) : <span className="text-zinc-700">—</span>}
                  </td>
                  {fechas.map((f) => {
                    const cell = matriz[e.id]?.[f];
                    if (!cell?.ruta_paradas?.length) {
                      const d = new Date(f + "T00:00:00");
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return <td key={f} className={`text-center px-1 py-2 border-r border-zinc-800/60 ${isWeekend ? "bg-zinc-900/30" : ""}`}>
                        <span className="text-zinc-800">·</span>
                      </td>;
                    }
                    return (
                      <td key={f} className="text-center px-1 py-1 border-r border-zinc-800/60">
                        {cell.ruta_paradas.map((rp, i) => (
                          <Link
                            key={i} to={`/rutas/${rp.ruta_id}`}
                            className="inline-flex items-center justify-center bg-emerald-500/20 text-emerald-200 rounded font-bold text-[10px] h-6 px-1.5 hover:bg-emerald-500/40 transition-colors mb-0.5"
                            title={`${rp.ruta_codigo} · ${rp.n_comp} componente(s)`}
                            data-testid={`cron-cell-${e.id}-${f}`}
                          >
                            {rp.n_comp}
                          </Link>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-400 flex items-center gap-4 flex-wrap">
        <span><span className="inline-block w-3 h-3 bg-amber-500/30 rounded mr-1 align-middle"></span>Cola pendiente (sin ruta asignada)</span>
        <span><span className="inline-block w-3 h-3 bg-emerald-500/30 rounded mr-1 align-middle"></span>Componentes planificados en una ruta</span>
        <span>El número en cada celda = componentes incluidos en esa parada</span>
        <Link to="/pendientes-montar" className="text-cyan-400 hover:text-cyan-300 ml-auto flex items-center gap-1">
          Ver detalle de pendientes <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
